import { apiClient } from './apiClient';

export interface DashboardOverview {
  total_power: number;
  equipment_status: {
    inverters: number;
    meters: number;
    weather_stations: number;
    total_active: number;
  };
  alerts: {
    critical: number;
    warning: number;
    info: number;
  };
  last_updated: string;
}

export interface Equipment {
  id: string;
  name: string;
  status: 'active' | 'idle' | 'offline';
  current_power?: number;
  last_update: string | null;
}

export interface EquipmentList {
  inverters: Equipment[];
  meters: Equipment[];
  weather_stations: Equipment[];
}

export interface PowerTrend {
  hour: string;
  avg_power: number;
  max_power: number;
  min_power: number;
}

export interface WeatherData {
  [key: string]: {
    value: number;
    unit: string;
    timestamp: string;
  };
}

export interface EquipmentParameter {
  name: string;
  value: number;
  unit: string;
  timestamp: string;
  quality: string;
}

export interface EquipmentDetails {
  equipment_id: string;
  parameters: EquipmentParameter[];
}

export const monitoringApi = {
  // Get dashboard overview
  getDashboardOverview: async (): Promise<DashboardOverview> => {
    const response = await apiClient.get('/api/monitoring/dashboard/overview/');
    return response.data;
  },

  // Get equipment list
  getEquipmentList: async (): Promise<EquipmentList> => {
    const response = await apiClient.get('/api/monitoring/equipment/');
    return response.data;
  },

  // Get power generation trends
  getPowerTrends: async (hours: number = 24): Promise<PowerTrend[]> => {
    const response = await apiClient.get(`/api/monitoring/power-trends/?hours=${hours}`);
    return response.data;
  },

  // Get weather data
  getWeatherData: async (): Promise<WeatherData> => {
    const response = await apiClient.get('/api/monitoring/weather/');
    return response.data;
  },

  // Get equipment parameters
  getEquipmentParameters: async (equipmentId: string): Promise<EquipmentDetails> => {
    const response = await apiClient.get(`/api/monitoring/equipment/${equipmentId}/parameters/`);
    return response.data;
  },

  // Get measurements with filters
  getMeasurements: async (params: {
    equipment_id?: string;
    parameter_name?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) queryParams.append(key, value.toString());
    });
    
    const response = await apiClient.get(`/api/monitoring/measurements/?${queryParams}`);
    return response.data;
  }
};